import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { FILE_URL, api } from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { Button } from "@/components/ui/button";
import { ShoppingBasket, Trash2, ClipboardList, Check, ChefHat, Share2, Plus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import BasketShareSheet from "@/components/BasketShareSheet";

function extractIngredients(context = "") {
  const lines = context.split("\n");
  const out = [];
  let inIng = false;
  for (const raw of lines) {
    const heading = /^#{1,3}\s+(.+?)\s*#*\s*$/.exec(raw);
    if (heading) {
      inIng = /ingredient/i.test(heading[1]);
      continue;
    }
    if (inIng) {
      const t = raw.trim();
      if (!t) continue;
      out.push(t.replace(/^([*\-+]|\d+\.)\s+/, "").replace(/[*_`]/g, ""));
    }
  }
  return out;
}

export default function Basket() {
  const { items, add, remove, clear, reorder } = useBasket();
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  // Handle inbound shared basket link — fetch shared recipes and show a soft banner
  const shareParam = params.get("share");
  const [shared, setShared] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);

  useEffect(() => {
    if (!shareParam) { setShared([]); return; }
    setSharedLoading(true);
    api.get("/ideas", { params: { ids: shareParam } })
      .then(({ data }) => setShared(data.filter((d) => d.category === "Recipes")))
      .catch(() => setShared([]))
      .finally(() => setSharedLoading(false));
  }, [shareParam]);

  const addAllShared = () => {
    let added = 0;
    for (const idea of shared) {
      if (!items.some((i) => i.id === idea.id)) { add(idea); added += 1; }
    }
    toast(added ? `Added ${added} to your basket.` : "All shared recipes are already in your basket.");
    setParams({}, { replace: true });
  };

  const grouped = useMemo(
    () => items.map((r) => ({ ...r, ingredients: extractIngredients(r.context || "") })).filter((r) => r.ingredients.length > 0),
    [items]
  );
  const grandCount = grouped.reduce((n, r) => n + r.ingredients.length, 0);

  const copyAll = async () => {
    if (grouped.length === 0) return;
    const lines = [];
    lines.push("Meal plan — shopping list");
    lines.push("");
    for (const r of grouped) {
      lines.push(`${r.title}${r.servings ? ` (${r.servings} servings)` : ""}`);
      for (const ing of r.ingredients) lines.push(`  • ${ing}`);
      lines.push("");
    }
    lines.push("— from Willoah · real life. real ideas.");
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      toast("Whole basket copied — paste into your notes app.");
      setTimeout(() => setCopied(false), 2200);
    } catch { toast("Could not copy. Try again or select manually."); }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="accent-label text-[#B79256]">meal plan</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-3 leading-none">Your Basket</h1>
          <p className="text-[#2A2823]/60 mt-3 max-w-lg">
            Drop recipes here from The Grove, then copy every ingredient at once for the weekly shop. Kept locally — nothing tracked.
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="basket-copy-all"
              onClick={copyAll}
              disabled={grandCount === 0}
              className={`rounded-full h-11 px-6 shadow-none ${copied ? "bg-[#4E5A3D] text-[#FBF8F1]" : "bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830]"}`}
            >
              {copied ? <><Check className="w-4 h-4 mr-2" strokeWidth={2}/> Copied</> : <><ClipboardList className="w-4 h-4 mr-2" strokeWidth={1.75}/> Copy all ingredients ({grandCount})</>}
            </Button>
            <Button
              data-testid="basket-share-btn"
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="rounded-full h-11 px-5 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]"
              title="Share this basket"
            >
              <Share2 className="w-4 h-4 mr-2" strokeWidth={1.75}/> Share basket
            </Button>
            <Button
              data-testid="basket-clear"
              onClick={() => { clear(); toast("Basket emptied."); }}
              variant="outline"
              className="rounded-full h-11 px-5 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.75}/> Empty
            </Button>
          </div>
        )}
      </div>

      {/* Shared-link banner */}
      {shareParam && (
        <section data-testid="shared-basket-banner" className="mt-10 rounded-3xl border border-[#4E5A3D]/30 bg-[#4E5A3D]/6 p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="accent-label text-[#4E5A3D]">shared with you</div>
              <p className="font-serif-display text-2xl text-[#2A2823] leading-snug mt-1">
                {sharedLoading ? "Loading shared recipes…" : `${shared.length} honest ${shared.length === 1 ? "recipe" : "recipes"} from a friend`}
              </p>
              <p className="text-sm text-[#2A2823]/60 mt-1">Every ingredient below is ready to copy into your notes app.</p>
            </div>
            {shared.length > 0 && (
              <Button
                data-testid="add-all-shared"
                onClick={addAllShared}
                className="rounded-full h-11 px-5 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={2}/> Add all to my basket
              </Button>
            )}
          </div>
          {shared.length > 0 && (
            <ul className="mt-5 grid sm:grid-cols-2 gap-4">
              {shared.map((r) => (
                <li key={r.id} data-testid={`shared-item-${r.id}`} className="rounded-2xl bg-white border border-[#B79256]/25 p-4 flex gap-3">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#B79256]/10 flex-shrink-0">
                    {r.photo_path && <img src={FILE_URL(r.photo_path)} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={`/idea/${r.id}`} className="font-serif-display text-lg text-[#2A2823] leading-tight hover:text-[#4E5A3D]">
                      {r.title}
                    </Link>
                    <div className="text-xs text-[#2A2823]/60 mt-1">by {r.author_name}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {items.length === 0 && !shareParam ? (
        <div data-testid="basket-empty" className="mt-12 text-center py-24 border border-dashed border-[#B79256]/40 rounded-3xl">
          <ShoppingBasket className="w-10 h-10 mx-auto text-[#B79256]" strokeWidth={1.5} />
          <p className="font-serif-display text-3xl text-[#4E5A3D] mt-4">Your basket is empty.</p>
          <p className="text-[#2A2823]/60 mt-3">
            Wander through <Link to="/grove" className="underline text-[#4E5A3D]">The Grove</Link> and tap the basket icon on any recipe to fill it up.
          </p>
        </div>
      ) : items.length > 0 && (
        <div className="mt-10 space-y-6" data-testid="basket-items">
          <p className="text-xs text-[#2A2823]/50 accent-label">drag to reorder — cooking order flows into your shopping list</p>
          {items.map((r, index) => {
            const ings = extractIngredients(r.context || "");
            const isDragOver = overIndex === index && dragIndex.current !== index;
            return (
              <article
                key={r.id}
                data-testid={`basket-item-${r.id}`}
                draggable
                onDragStart={(e) => {
                  dragIndex.current = index;
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", String(index)); } catch {}
                }}
                onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
                onDragLeave={() => setOverIndex((prev) => (prev === index ? null : prev))}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragIndex.current;
                  dragIndex.current = null;
                  setOverIndex(null);
                  if (from !== null && from !== index) reorder(from, index);
                }}
                onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
                className={`rounded-3xl border bg-white p-4 sm:p-6 flex flex-col sm:flex-row gap-4 transition-all ${
                  isDragOver ? "border-[#4E5A3D] bg-[#4E5A3D]/5 -translate-y-0.5" : "border-[#B79256]/25"
                }`}
              >
                <button
                  type="button"
                  data-testid={`basket-drag-${r.id}`}
                  aria-label={`Reorder ${r.title}`}
                  className="hidden sm:flex items-center justify-center self-stretch cursor-grab active:cursor-grabbing text-[#B79256]/60 hover:text-[#4E5A3D] px-2 -ml-2 touch-none"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <GripVertical className="w-5 h-5" strokeWidth={1.75}/>
                </button>
                <div className="w-full sm:w-32 h-32 rounded-2xl overflow-hidden bg-[#B79256]/10 border border-[#B79256]/20 flex-shrink-0">
                  {r.photo_path ? (
                    <img src={FILE_URL(r.photo_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ChefHat className="w-6 h-6 text-[#B79256]"/></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3">
                    <span className="accent-label text-[#B79256] text-xs">#{index + 1}</span>
                    <Link to={`/idea/${r.id}`} className="font-serif-display text-2xl text-[#2A2823] leading-tight hover:text-[#4E5A3D] transition-colors">
                      {r.title}
                    </Link>
                  </div>
                  <div className="text-xs text-[#2A2823]/60 mt-1">
                    by {r.author_name}
                    {r.servings && <> · <b className="text-[#4E5A3D]">{r.servings}</b> servings</>}
                    {r.total_time && <> · {r.total_time}</>}
                  </div>
                  {ings.length > 0 ? (
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#2A2823]/85 list-disc marker:text-[#B79256] pl-5">
                      {ings.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                  ) : (
                    <p className="mt-3 text-sm italic text-[#2A2823]/50">No ingredients heading in this branch — copy-all will skip it.</p>
                  )}
                </div>
                <div className="flex sm:flex-col items-center gap-2 sm:self-start">
                  {/* Mobile move buttons */}
                  <button
                    type="button"
                    data-testid={`basket-move-up-${r.id}`}
                    onClick={() => reorder(index, index - 1)}
                    disabled={index === 0}
                    className="sm:hidden p-3 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#B79256]/12 disabled:opacity-30 transition-colors"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    data-testid={`basket-move-down-${r.id}`}
                    onClick={() => reorder(index, index + 1)}
                    disabled={index === items.length - 1}
                    className="sm:hidden p-3 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#B79256]/12 disabled:opacity-30 transition-colors"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    data-testid={`basket-remove-${r.id}`}
                    onClick={() => { remove(r.id); toast("Removed from basket."); }}
                    className="p-3 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#B79256]/12 transition-colors"
                    aria-label={`Remove ${r.title} from basket`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.75}/>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <BasketShareSheet
        ids={items.map((i) => i.id)}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </main>
  );
}
